import { API_ENDPOINTS } from "../config/api"

// Define interfaces for API requests and responses
export interface LoginCredentials {
  email: string
  password: string
}

export interface SyncConfigData {
  hs_to_sf: {
        create_enabled: boolean;
        update_enabled: boolean;
        delete_enabled: boolean;
        restore_enabled: boolean;
    };
    sf_to_hs: {
        create_enabled: boolean;
        update_enabled: boolean;
        delete_enabled: boolean;
        restore_enabled: boolean;
  };
}

export interface ConnectorsConfig {
  tenantId: string
  connectorId: number
  syncDirection: string
  fieldMappings: object
  syncConfig: SyncConfigData
  activate: boolean
}

export interface ChangePassword {
  current_password: string
  new_password: string
  confirm_password: string
}

export interface getAuthTokenRequest{
  tanentConnectorId : number
  tanentId: number
}

// export interface ProfileFormData {
//   first_name: string
//   last_name: string
//   email: string
//   company: string
//   company_size: string
//   industry: string
//   country: string
//   state: string
//   city: string
//   job_title: string
//   organization_name: string
// }

export interface RegisterUserData {
  first_name: string
  last_name: string
  email: string
  company: string
  company_size: string
  industry: string
  job_title: string
  country: string
  state: string
  city: string
  password: string
  confirm_password?: string
  terms: boolean
}

export interface ApiResponse<T = any> {
  success: boolean
  message: string
  data: T | null
}

export interface Organisation {
  organization_id: string
  name: string
  description: string
  role: string
}

export interface User {
  id: string
  first_name: string
  last_name: string
  email: string
  company: string
  company_size: string
  industry: string
  country: string
  city: string
  email_verified: Number
  status: string
  trial_expiration: string
  state: string
  created_at: string
  organizations: Organisation
}

export interface LoginResponseData {
  access_token: string
  refresh_token: string
  token_type: string
  expires_in: string
  user: User
}

export interface LoginResponse {
  status: Number
  message: string
  data: LoginResponseData
}

export interface UserProfile extends User {
  company: string
  company_size: string
  industry: string
  job_title: string
  country: string
  state: string
  city: string
  organization_name: string
}

interface RequestOptions extends RequestInit {
  headers?: Record<string, string>
  skipAuth?: boolean
  skipRetry?: boolean
}

export interface Connector {
  id: string
  name: string
  source_system: string
  source_object: string
  target_system: string
  target_object: string
  supported_directions: string
}

export interface ApiRes {
  status: number
  message: string
  data: Object | String | Array<Object>
}

// Enhanced error interface for better error handling
export interface ApiError {
  status: number
  message: string
  details?: any
  userMessage: string
}

class ApiService {
  private isRefreshing = false
  private failedQueue: Array<{
    resolve: (value: any) => void
    reject: (error: any) => void
  }> = []
  private userProfileCache: UserProfile | null = null
  private profileCacheExpiry: number = 0
  private readonly CACHE_DURATION = 5 * 60 * 1000 // 5 minutes

  // Enhanced error handling method
  private handleApiError(error: any, context: string = ''): ApiError {
    console.error(`API Error in ${context}:`, error)

    // Handle different types of errors
    if (error instanceof TypeError && error.message && error.message.includes('fetch')) {
      return {
        status: 0,
        message: 'Network error',
        userMessage: 'Unable to connect to the server. Please check your internet connection.',
        details: error
      }
    }

    if (error.status) {
      // API returned an error response
      const userMessage = this.getUserFriendlyMessage(error.status, error.message)
      return {
        status: error.status,
        message: error.message || 'Unknown error',
        userMessage,
        details: error
      }
    }

    // Generic error
    return {
      status: 500,
      message: error.message || 'Unknown error occurred',
      userMessage: 'Something went wrong. Please try again later.',
      details: error
    }
  }

  private getUserFriendlyMessage(status: number, message: string): string {
    switch (status) {
      case 400:
        if ((message && message.includes('tenantId')) || (message && message.includes('connectorId'))) {
          return 'Required information is missing. Please ensure all fields are filled.'
        }
        return 'Invalid request. Please check your input and try again.'
      case 401:
        return 'Your session has expired. Please log in again.'
      case 403:
        return 'You do not have permission to perform this action.'
      case 404:
        return 'The requested resource was not found.'
      case 429:
        return 'Too many requests. Please wait a moment and try again.'
      case 500:
        return 'Server error. Please try again later.'
      default:
        return 'An unexpected error occurred. Please try again.'
    }
  }

  // Validation methods
  private validateRequiredParams(params: Record<string, any>, requiredFields: string[]): void {
    const missingFields = requiredFields.filter(field => 
      params[field] === undefined || params[field] === null || params[field] === ''
    )
    
    if (missingFields.length > 0) {
      throw new Error(`Missing required parameters: ${missingFields.join(', ')}`)
    }
  }

  // Token management methods
  private getToken(): string | null {
    return localStorage.getItem("token")
  }

  private getRefreshToken(): string | null {
    return localStorage.getItem("refresh_token")
  }

  private getTenantId(): string | null {
    const hubspotTenantId = localStorage.getItem("hubspot_tenant_id")
    if (hubspotTenantId) {
      try {
        return hubspotTenantId || null
      } catch (error) {
        console.warn('Failed to parse user data:', error)
        return null
      }
    }
    return null
  }

  private setTokens(accessToken: string, refreshToken?: string): void {
    localStorage.setItem("token", accessToken)
    if (refreshToken) {
      localStorage.setItem("refresh_token", refreshToken)
    }
    // Set token expiry (assuming 1 hour for access token)
    const expiryTime = Date.now() + (60 * 60 * 1000)
    localStorage.setItem("token_expiry", expiryTime.toString())
  }

  private clearTokens(): void {
    localStorage.removeItem("token")
    localStorage.removeItem("refresh_token")
    localStorage.removeItem("token_expiry")
    localStorage.removeItem("userData")
    this.userProfileCache = null
    this.profileCacheExpiry = 0
  }

  private isTokenExpired(): boolean {
    const expiry = localStorage.getItem("token_expiry")
    if (!expiry) return true
    return Date.now() > parseInt(expiry)
  }

  // Token refresh method
  private async refreshToken(): Promise<string> {
    const refreshToken = this.getRefreshToken()
    if (!refreshToken) {
      throw new Error("No refresh token available")
    }

    try {
      const response = await fetch(API_ENDPOINTS.REFRESH_TOKEN || `${API_ENDPOINTS.LOGIN}/refresh`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      })

      if (!response.ok) {
        throw new Error("Token refresh failed")
      }

      const data = await response.json()
      const newAccessToken = data.data?.access_token || data.access_token
      const newRefreshToken = data.data?.refresh_token || data.refresh_token

      this.setTokens(newAccessToken, newRefreshToken)
      return newAccessToken
    } catch (error) {
      this.clearTokens()
      throw error
    }
  }

  // Process failed queue after token refresh
  private processQueue(error: any, token: string | null = null): void {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error)
      } else {
        resolve(token)
      }
    })

    this.failedQueue = []
  }

  async request<T>(url: string, options: RequestOptions = {}): Promise<T> {
    const { skipAuth = false, skipRetry = false, ...requestOptions } = options

    const config: RequestOptions = {
      headers: {
        "ngrok-skip-browser-warning": "true",
        "Content-Type": "application/json",
        "access-control-allow-origin": "*",
        ...requestOptions.headers,
      },
      ...requestOptions,
    }

    // Add authorization header if not skipping auth
    if (!skipAuth) {
      const token = this.getToken()
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`
      }
    }

    try {
      const response = await fetch(url, config)

      // Handle 401 Unauthorized
      if (response.status === 401 && !skipAuth && !skipRetry) {
        const token = this.getToken()
        
        if (!token) {
          this.redirectToLogin()
          throw this.handleApiError({ status: 401, message: 'Authentication required' }, 'request')
        }

        // If we're already refreshing, queue this request
        if (this.isRefreshing) {
          return new Promise((resolve, reject) => {
            this.failedQueue.push({ resolve, reject })
          }).then(() => {
            return this.request<T>(url, { ...options, skipRetry: true })
          })
        }

        // Try to refresh the token
        this.isRefreshing = true

        try {
          const newToken = await this.refreshToken()
          this.processQueue(null, newToken)
          return this.request<T>(url, { ...options, skipRetry: true })
        } catch (refreshError) {
          this.processQueue(refreshError, null)
          this.redirectToLogin()
          throw this.handleApiError({ status: 401, message: 'Session expired' }, 'token refresh')
        } finally {
          this.isRefreshing = false
        }
      }
      
      const data = await response.json()

      if (!response.ok) {
        throw this.handleApiError({
          status: response.status,
          message: data.message || data.error || 'Request failed'
        }, 'request')
      }

      return data as T
    } catch (error: any) {
      // Don't re-wrap already handled API errors
      if (error.userMessage) {
        throw error
      }
      
      throw this.handleApiError(error, 'request')
    }
  }

  private redirectToLogin(): void {
    if (window.location.pathname !== "/login") {
      window.location.href = "/login"
    }
  }

  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      const response = await this.request<LoginResponse>(API_ENDPOINTS.LOGIN, {
        method: "POST",
        body: JSON.stringify(credentials),
        skipAuth: true,
      })

      // Store tokens after successful login
      if (response.data) {
        this.setTokens(response.data.access_token, response.data.refresh_token)
        localStorage.setItem('userData', JSON.stringify(response.data.user))
        this.userProfileCache = response.data.user as UserProfile
        this.profileCacheExpiry = Date.now() + this.CACHE_DURATION
      }

      return response
    } catch (error) {
      throw this.handleApiError(error, 'login')
    }
  }

  async register(userData: RegisterUserData): Promise<ApiResponse> {
    try {
      return await this.request<ApiResponse>(API_ENDPOINTS.REGISTER, {
        method: "POST",
        body: JSON.stringify(userData),
        skipAuth: true,
      })
    } catch (error) {
      throw this.handleApiError(error, 'register')
    }
  }

  // Enhanced getConnectors with better error handling
  async getConnectors(): Promise<ApiRes> {
    try {
      return await this.request<ApiRes>(API_ENDPOINTS.GET_ALL_CONNECTORS, {
        method: "GET",
        skipAuth: false,
      })
    } catch (error) {
      throw this.handleApiError(error, 'getConnectors')
    }
  }

  async getSavedConnectors(): Promise<ApiRes> {
    try {
      return await this.request<ApiRes>(API_ENDPOINTS.SAVED_CONNECTORS, {
        method: "GET",
        skipAuth: false,
      })
    } catch (error) {
      throw this.handleApiError(error, 'getConnectors')
    }
  }

  async getSavedConnectorsFields(connectorId: number): Promise<ApiRes> {
    try {
      // Validate required parameters
      this.validateRequiredParams({  connectorId }, ['connectorId'])

      return await this.request<ApiRes>(API_ENDPOINTS.SAVED_CONNECTORS_FIELDS + `/${connectorId}`, {
        method: "GET",
        skipAuth: false,
      })
    } catch (error) {
      throw this.handleApiError(error, 'getConnectors')
    }
  }

    // Enhanced getAllFields with parameter validation
  async getAllFields(tenantId: number, connectorId: number): Promise<ApiRes> {
    try {
      // Validate required parameters
      this.validateRequiredParams({ tenantId, connectorId }, ['tenantId', 'connectorId'])

      // Auto-fill tenantId if not provided but available in storage
      const finalTenantId = tenantId || this.getTenantId()
      if (!finalTenantId) {
        throw new Error('Tenant ID is required but not available')
      }

      const url = `${API_ENDPOINTS.OBJECT_FIELD_MAPPING}?tenantId=${finalTenantId}&connectorId=${connectorId}`
      
      return await this.request<ApiRes>(url, {
        method: "GET",
        skipAuth: false,
      })
    } catch (error) {
      throw this.handleApiError(error, 'getAllFields')
    }
  }

  async getAuthStatus(tenantId: number, tanentConnectorId: number): Promise<ApiRes> {
    try {
      // Validate required parameters
      this.validateRequiredParams({ tenantId, tanentConnectorId }, ['tenantId', 'tanentConnectorId'])

      // Auto-fill tenantId if not provided but available in storage
      const finalTenantId = tenantId || this.getTenantId()
      if (!finalTenantId) {
        throw new Error('Tenant ID is required but not available')
      }

      const url = `${API_ENDPOINTS.GET_SYNC_AUTH_STATUS}?tenant_connector_id=${tanentConnectorId}&tenant_id=${finalTenantId}`
      
      return await this.request<ApiRes>(url, {
        method: "GET",
        skipAuth: false,
      })
    } catch (error) {
      throw this.handleApiError(error, 'getAllFields')
    }
  }
  async getConnectorDetail(tanentConnectorId: number): Promise<ApiRes> {
    try {
      // Validate required parameters
      this.validateRequiredParams({tanentConnectorId }, [ 'tanentConnectorId'])

      const url = `${API_ENDPOINTS.CONNECTOR_CONFIG}/${tanentConnectorId}`
      
      return await this.request<ApiRes>(url, {
        method: "GET",
        skipAuth: false,
      })
    } catch (error) {
      throw this.handleApiError(error, 'getAllFields')
    }
  }

  async authoriseHubspot(): Promise<ApiRes> {
    try {
      return await this.request<ApiRes>(API_ENDPOINTS.AUTHORISE_HUBSPOT, {
        method: "GET",
        skipAuth: false,
      })
    } catch (error) {
      throw this.handleApiError(error, 'authoriseHubspot')
    }
  }


  async saveConnectorConfig(userData: ConnectorsConfig): Promise<ApiResponse> {
    try {
      return await this.request<ApiResponse>(API_ENDPOINTS.CONNECTOR_CONFIG, {
        method: "POST",
        body: JSON.stringify(userData),
        skipAuth: false,
      })
    } catch (error) {
      throw this.handleApiError(error, 'saveConnectorConfig')
    }
  }

  async authoriseSalesforce(tenantId?: number): Promise<ApiRes> {
    try {
      const finalTenantId = tenantId || this.getTenantId()
      if (!finalTenantId) {
        throw new Error('Tenant ID is required')
      }

      return await this.request<ApiRes>(`${API_ENDPOINTS.AUTHORISE_SALESFORCE}?tenant_id=${finalTenantId}`, {
        method: "GET",
        skipAuth: false,
      })
    } catch (error) {
      throw this.handleApiError(error, 'authoriseSalesforce')
    }
  }

  async forgotPassword(email: string): Promise<ApiResponse> {
    try {
      this.validateRequiredParams({ email }, ['email'])
      
      return await this.request<ApiResponse>(API_ENDPOINTS.FORGOT_PASSWORD, {
        method: "POST",
        body: JSON.stringify({ email }),
        skipAuth: true,
      })
    } catch (error) {
      throw this.handleApiError(error, 'forgotPassword')
    }
  }

  async resetPassword(token: string, new_password: string, confirm_password: string): Promise<ApiRes> {
    try {
      this.validateRequiredParams({ token, new_password, confirm_password }, ['token', 'new_password', 'confirm_password'])
      
      return await this.request<ApiRes>(API_ENDPOINTS.RESET_PASSWORD, {
        method: "POST",
        body: JSON.stringify({ token, new_password, confirm_password }),
        skipAuth: true,
      })
    } catch (error) {
      throw this.handleApiError(error, 'resetPassword')
    }
  }

  async changePassword(data: ChangePassword): Promise<ApiResponse> {
    try {
      this.validateRequiredParams(data, ['current_password', 'new_password', 'confirm_password'])
      
      return await this.request<ApiResponse>(API_ENDPOINTS.USER_CHANGE_PASSWORD, {
        method: "POST",
        body: JSON.stringify(data),
        skipAuth: false,
      })
    } catch (error) {
      throw this.handleApiError(error, 'changePassword')
    }
  }

  async verifyEmail(token: string): Promise<ApiResponse> {
    try {
      this.validateRequiredParams({ token }, ['token'])
      
      return await this.request<ApiResponse>(API_ENDPOINTS.VERIFY_EMAIL, {
        method: "POST",
        body: JSON.stringify({ token }),
        skipAuth: true,
      })
    } catch (error) {
      throw this.handleApiError(error, 'verifyEmail')
    }
  }

  async getUserProfile(forceRefresh = false): Promise<UserProfile> {
      try {
      const profile = await this.request<UserProfile>(API_ENDPOINTS.USER_PROFILE)
      this.userProfileCache = profile
      this.profileCacheExpiry = Date.now() + this.CACHE_DURATION
      return profile
    } catch (error) {
      if (this.userProfileCache && !forceRefresh) {
        console.warn("Failed to fetch fresh profile, returning cached data")
        return this.userProfileCache
      }
      throw this.handleApiError(error, 'getUserProfile')
    }
  }

  async updateUserProfile(userProfile: any): Promise<ApiResponse> {
    try {
      const profile = await this.request<ApiResponse>(API_ENDPOINTS.USER_PROFILE, {
        method: "PUT",
        body: JSON.stringify(userProfile),
        skipAuth: false,
      })
      
      this.profileCacheExpiry = Date.now() + this.CACHE_DURATION
      return profile
    } catch (error) {
      throw this.handleApiError(error, 'updateUserProfile')
    }
  }

  async updateConnectorConfig(userData: any): Promise<ApiResponse> {
    try {
      const respose = await this.request<ApiResponse>(API_ENDPOINTS.CONNECTOR_CONFIG + `/${userData.tenant_connector_id}`, {
        method: "PUT",
        body: JSON.stringify(userData),
        skipAuth: false,
      })
      
      // this.profileCacheExpiry = Date.now() + this.CACHE_DURATION
      return respose
    } catch (error) {
      throw this.handleApiError(error, 'updateConnectorConfig')
    }
  }

  async deleteConnectorConfig(userData: any): Promise<ApiResponse> {
    try {
      const respose = await this.request<ApiResponse>(API_ENDPOINTS.CONNECTOR_CONFIG + `/${userData.connectorId}`, {
        method: "DELETE",
        skipAuth: false,
      })
      
      // this.profileCacheExpiry = Date.now() + this.CACHE_DURATION
      return respose
    } catch (error) {
      throw this.handleApiError(error, 'updateConnectorConfig')
    }
  }

  // Method to check if user is authenticated
  isAuthenticated(): boolean {
    const token = this.getToken()
    const refreshToken = this.getRefreshToken()
    return !!(token || refreshToken)
  }

  // Method to check if token is valid without making API call
  async validateToken(): Promise<boolean> {
    if (!this.getToken()) {
      return false
    }

    if (this.isTokenExpired() && this.getRefreshToken()) {
      try {
        await this.refreshToken()
        return true
      } catch (error) {
        return false
      }
    }

    if (!this.isTokenExpired()) {
      return true
    }

    try {
      await this.getUserProfile()
      return true
    } catch (error) {
      return false
    }
  }

  // Method to logout user
  async logout(): Promise<boolean> {
    try {
      let refresh_token = this.getRefreshToken()
      
      await this.request<ApiResponse>(API_ENDPOINTS.USER_lOGOUT, {
        method: "POST",
        body: JSON.stringify({ refresh_token  }),
        skipAuth: false,
      })
      localStorage.removeItem("refresh_token")
      localStorage.removeItem("token_type")
      localStorage.removeItem("token_expiry")
      localStorage.removeItem("connectorFlowStep")
      localStorage.removeItem("fieldMappings")
      localStorage.removeItem("hubspot_connected")
      localStorage.removeItem("hubspot_tenant_id")
      localStorage.removeItem("salesforce_connected")
      localStorage.removeItem("selectedConnector")
      localStorage.removeItem("selectedSyncDirection")
      localStorage.removeItem("selectedSyncOption")
      localStorage.removeItem("userData")
      this.clearTokens()
      this.redirectToLogin()
      return true
    } catch (error) {
      throw this.handleApiError(error, 'resetPassword')
    }

  }

  // Method to clear cache
  clearCache(): void {
    this.userProfileCache = null
    this.profileCacheExpiry = 0
  }

  // Method to preload user profile
  async preloadUserProfile(): Promise<void> {
    try {
      await this.getUserProfile()
    } catch (error) {
      console.warn("Failed to preload user profile:", error)
    }
  }

  // Utility method to get user-friendly error message
  getErrorMessage(error: any): string {
    if (error.userMessage) {
      return error.userMessage
    }
    return 'An unexpected error occurred. Please try again.'
  }
}

export default new ApiService()